/**
 * ForgotUsernamePage.tsx — public "Forgot your username?" page.
 *
 * Salesforce-style login: users sign in with a unique USERNAME, and one email
 * address may own several usernames. This page emails every username linked
 * to an address (one email per workspace) via
 * POST /api/v1/public/forgot-username.
 *
 * The backend always answers 202 with the same message whether or not the
 * email exists (no account enumeration), so this page shows that message
 * verbatim.
 *
 * AuthContext skips Keycloak initialisation on this path (it is public), so
 * "Back to sign in" is a full page load to "/" which re-runs the login flow.
 */
import { useState } from "react";
import type { FormEvent } from "react";
import { ArrowLeft, Loader2, Mail, MailCheck } from "lucide-react";

import { publicApi } from "@/services/apiClient";
import { apiErrorDetail } from "@/lib/username";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OutrenaLockup } from "@/components/OutrenaLogo";

export function ForgotUsernamePage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value || !value.includes("@")) {
      setError("Enter a valid email address.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await publicApi.forgotUsername(value);
      setDoneMessage(res.message);
    } catch (err) {
      setError(apiErrorDetail(err, "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(60rem 40rem at 80% -10%, rgba(99,102,241,0.18), transparent 60%), radial-gradient(50rem 30rem at 10% 110%, rgba(16,185,129,0.14), transparent 60%)",
        }}
      />

      <Card className="relative z-10 w-full max-w-md border-slate-800 bg-slate-900/80 text-slate-100 shadow-2xl backdrop-blur">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center pt-2">
            <OutrenaLockup width={160} />
          </div>
          <CardDescription className="text-slate-400">
            Forgot your username?
          </CardDescription>
        </CardHeader>

        <CardContent>
          {doneMessage ? (
            <div className="space-y-3 text-center" role="status">
              <MailCheck className="mx-auto h-10 w-10 text-emerald-400" />
              <p className="text-sm text-slate-200">{doneMessage}</p>
              <p className="text-xs text-slate-500">
                One email address can have several usernames — the email lists all of them.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <p className="text-sm text-slate-400">
                Enter the email address on your Outrena account. We'll email you
                every username linked to it.
              </p>
              <div className="space-y-2">
                <Label htmlFor="fu-email" className="text-slate-300">
                  Email address
                </Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <Input
                    id="fu-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-slate-700 bg-slate-800/60 pl-9 text-slate-100"
                  />
                </div>
                {error && (
                  <p className="text-xs text-red-400" role="alert">
                    {error}
                  </p>
                )}
              </div>
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Email my username(s)
              </Button>
            </form>
          )}
        </CardContent>

        <CardFooter className="justify-center">
          {/* Full page load (not a router Link): Keycloak initialisation is
              skipped on this public page, so returning must re-run it. */}
          <a
            href="/"
            className="flex items-center gap-1.5 text-xs text-slate-400 underline-offset-2 hover:text-slate-200 hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </a>
        </CardFooter>
      </Card>
    </div>
  );
}
